import { MenuItem, ModifierDetail, ModifierGroup, ModifierGroupWrapper } from '@core/models';
import { ModifierPriceValue, ModifierQuantityPrice, ModifierSelectionState } from '../interfaces';

export function calculateModifierQuantityPrice(
	currentQuantity: number,
	change: -1 | 1,
	unitPrice: ModifierPriceValue
): ModifierQuantityPrice {
	const quantity = currentQuantity + change;
	const priceQuantity = change === -1 && quantity === 0 ? 1 : quantity;

	return { quantity, price: toBigInt(unitPrice) * BigInt(priceQuantity) };
}

export function createNoAddModifierPatch(
	detail: ModifierSelectionState,
	isSelected: boolean
): ModifierSelectionState {
	return {
		noAdd: isSelected,
		precio: toBigInt(detail.precioInicial ?? detail.precioBase),
		cantidad: 0,
		addExtra: false,
		isFree: false,
	};
}

export function createAddExtraModifierPatch(
	detail: ModifierSelectionState,
	isSelected: boolean,
	freeModifierLimit: number,
	selectedFreeModifiers: number
): ModifierSelectionState {
	return {
		addExtra: isSelected,
		precio: toBigInt(detail.precioInicial ?? detail.precioBase),
		cantidad: 0,
		noAdd: false,
		isFree: isSelected && freeModifierLimit > selectedFreeModifiers,
	};
}

export function getFreeModifierCount(group: ModifierGroup | null): number {
	return group?.detallesmodificadores.filter(detail => detail.isFree).length ?? 0;
}

export function getActiveModifierCount(group: ModifierGroup | null): number {
	return group?.detallesmodificadores.filter(detail => detail.addExtra).length ?? 0;
}

export function isModifierMinimumUnmet(group: ModifierGroup | null): boolean {
	return !!group && group.minimo > getActiveModifierCount(group);
}

export function isModifierMaximumReached(group: ModifierGroup | null): boolean {
	return !!group && group.maximo > 0 && group.maximo <= getActiveModifierCount(group);
}

export function hasUnmetModifierMinimum(groups: Array<ModifierGroup | null>): boolean {
	return groups.some(isModifierMinimumUnmet);
}

export function isSimpleMenuItem(item: MenuItem): boolean {
	const hasMultiplePrices = (item.productosCanal?.[0]?.precios?.length ?? 0) > 1;
	const hasModifiers = (item.gruposModificadores?.length ?? 0) > 0;
	return !hasMultiplePrices && !hasModifiers;
}

export function shouldDisableAddToOrder(
	stock: number,
	hasStockTracking: boolean,
	currentQuantity: number,
	quantityInCart: number
): boolean {
	return !!stock && hasStockTracking && currentQuantity + quantityInCart >= stock;
}

export function getProductModifiers(
	menuItem: MenuItem,
	isAdd: boolean = true
): ModifierDetail[] {
	const selectedDetails = (menuItem.gruposModificadores ?? [])
		.flatMap((group: ModifierGroupWrapper) => group.grupoModificador.detallesmodificadores)
		.filter(detail => (isAdd && detail?.addExtra) || (!isAdd && detail?.noAdd));

	return selectedDetails.map(detail => ({
		...detail,
		id: detail.idDetalleModificador,
		nombre: detail.modificador.nombre,
		cantidad: isAdd ? BigInt(detail.cantidad ?? 0n) : 0n,
		precio: isAdd ? BigInt(detail.precio ?? 0n) : 0n,
		precioBase: isAdd ? BigInt(detail.precioBase ?? 0n) : 0n,
		isFree: !!detail.isFree,
	}));
}

function toBigInt(value: ModifierPriceValue): bigint {
	if (value === null || value === undefined) return 0n;
	if (typeof value === 'bigint') return value;
	if (typeof value === 'number' && Number.isSafeInteger(value)) return BigInt(value);
	if (typeof value === 'string' && /^-?\d+$/.test(value)) return BigInt(value);
	throw new TypeError('Modifier prices must be integer values.');
}
